import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect } from "react";
import {
  useAddPaymentMutation,
  useUpdatePaymentMutation,
} from "src/api/settings/payments";
import { LoadingButton } from "@mui/lab";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";

const schema = yup.object().shape({
  name: yup.string().required("Required"),
  balance: yup.string(),
  isLinkedToAgg: yup.string().oneOf(["yes", "no"]).required("Required"),
  is_discount: yup.boolean().required("Required"),
  is_cash: yup.string().oneOf(["yes", "no"]).required("Required"),
});

interface FormInputs extends yup.InferType<typeof schema> {}

interface IModel {
  open: boolean;
  handleClose: () => void;
  paymentData?: any;
  refetch: Function;
  isCashOnly?: boolean; // when it comes from tree
}

const AddEditPayment = (props: IModel) => {
  const { open, handleClose, paymentData, refetch, isCashOnly } = props;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const mode = !paymentData ? "add" : "edit";
  const { control, handleSubmit, formState, reset, watch, setValue } =
    useForm<FormInputs>({
      mode: "onBlur",
      resolver: yupResolver(schema),
      defaultValues: {
        isLinkedToAgg: isCashOnly ? "yes" : "no",
        is_discount: false,
        is_cash: !isCashOnly ? "no" : "yes",
      },
    });
  const { t } = useTranslation("settings");
  const { t: generalT } = useTranslation("general");
  // APIS
  const { mutate, isPending, status, error } = useAddPaymentMutation();
  const {
    mutate: mutateUpdate,
    isPending: isPendingUpdate,
    status: statusUpdate,
    error: errorUpdate,
  } = useUpdatePaymentMutation();

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { name, balance, is_discount, isLinkedToAgg, is_cash } = data;
    const isDisValue: 1 | 0 = is_discount ? 1 : 0;
    const payload = {
      name,
      ...(!!balance && { balance: Number(balance) }),
      ...(mode === "edit" && isCashOnly
        ? {}
        : {
            is_cash: is_cash === "yes",
            is_discount: isDisValue,
            is_aggregator: isLinkedToAgg === "yes" ? 1 : 0,
          }),
    };
    if (mode === "add") {
      mutate(payload);
    } else {
      mutateUpdate({
        id: isCashOnly ? paymentData.source_id : paymentData.id,
        ...payload,
      });
    }
  };

  useEffect(() => {
    if (paymentData) {
      reset({
        name: paymentData.name,
        balance: paymentData.balance ?? paymentData?.start_balance ?? "", // start_balance for tree payments
        // type: isCashOnly || paymentData.is_cash ? "is_cash" : "is_credit",
        isLinkedToAgg: paymentData.is_aggregator ? "yes" : "no",
        is_cash: isCashOnly || paymentData.is_cash ? "yes" : "no",
        is_discount: !!paymentData.is_discount,
      });
    }
  }, [paymentData]);

  //Add user success and error handling
  useEffect(() => {
    if (status === "success") {
      handleClose();
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update user success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      handleClose();
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {mode === "add" ? t("addNewPayment") : t("editPayment")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  label={t("paymentName")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!formState.errors.name}
                  helperText={formState.errors.name?.message}
                  disabled={mode === "edit"}
                />
              )}
            />
            <Controller
              name="balance"
              control={control}
              render={({ field }) => (
                <TextField
                  type="number"
                  label={t("startBalance")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!formState.errors.balance}
                  helperText={
                    formState.errors.balance?.message ||
                    generalT("startBalanceHint")
                  }
                  FormHelperTextProps={{ sx: { whiteSpace: "pre-line" } }}
                  disabled={paymentData?.type === "other_revenue"}
                />
              )}
            />
            {!isCashOnly && (
              <Stack>
                <Controller
                  name="isLinkedToAgg"
                  control={control}
                  render={({ field }) => (
                    <FormControl error={!!formState.errors.isLinkedToAgg}>
                      <Stack direction={"row"} gap={2} alignItems={"center"}>
                        <FormLabel>{t("Is linked to aggregator?")}</FormLabel>

                        <RadioGroup
                          row
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value);
                            if (value === "no") {
                              setValue("is_cash", "no");
                            }
                          }}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label={t("yes")}
                            disabled={paymentData?.type === "other_revenue"}
                          />

                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label={t("no")}
                            disabled={paymentData?.type === "other_revenue"}
                          />
                        </RadioGroup>
                      </Stack>

                      <Typography variant="caption" color="error">
                        {formState.errors.isLinkedToAgg?.message}
                      </Typography>
                    </FormControl>
                  )}
                />

                {watch("isLinkedToAgg") === "yes" ? (
                  <Controller
                    name="is_cash"
                    control={control}
                    render={({ field }) => (
                      <FormControl error={!!formState.errors.is_cash}>
                        <Stack direction={"row"} gap={2} alignItems={"center"}>
                          <FormLabel>{t("isCash")}</FormLabel>

                          <RadioGroup row {...field}>
                            <FormControlLabel
                              value="yes"
                              control={<Radio />}
                              label={t("yes")}
                              disabled={paymentData?.type === "other_revenue"}
                            />

                            <FormControlLabel
                              value="no"
                              control={<Radio />}
                              label={t("no")}
                              disabled={paymentData?.type === "other_revenue"}
                            />
                          </RadioGroup>
                        </Stack>

                        <Typography variant="caption" color="error">
                          {formState.errors.is_cash?.message}
                        </Typography>
                      </FormControl>
                    )}
                  />
                ) : null}

                <Controller
                  name="is_discount"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ mt: 1.5 }}
                      control={<Checkbox {...field} color="primary" />}
                      label={t("loyaltyDiscount")}
                      checked={field.value}
                      disabled={
                        paymentData?.type === "other_revenue" ||
                        paymentData?.is_aggregator
                      }
                    />
                  )}
                />
              </Stack>
            )}
          </Stack>
          <Stack spacing={2} direction={"row"} mt={3}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              type="submit"
              loading={isPending || isPendingUpdate}
              variant="contained"
              fullWidth
            >
              {mode === "add" ? generalT("add") : generalT("save")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default AddEditPayment;